import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ThemePicker } from '@/components/preferences/ThemePicker';
import { LanguagePicker } from '@/components/preferences/LanguagePicker';
import { sanitizeNextPath } from '@/lib/nextPath';

/**
 * Public preferences page — language + theme only. Deliberately public so
 * a guest reading a `/share/study/<id>` page can flip language / theme
 * before or after signing in without hitting the auth gate on
 * `/account`. **Nothing personal ships here** — no profile, no team, no
 * user id. If a section requires identity, it belongs on `/account`, not
 * here.
 *
 * The theme + language setters are shared via `ThemePicker` /
 * `LanguagePicker` under `src/components/preferences/`, so a flip here
 * persists into the app once the user signs in and every future
 * settings surface stays in sync.
 *
 * A `?next=` param is honored by the "Back" button — the share page
 * that surfaces the settings link stamps its own path so the round
 * trip lands the user where they came from. Non-existent / stripped
 * `next` falls back to the home page.
 */
export const Route = createFileRoute('/preferences')({
  validateSearch: (search: Record<string, unknown>) => ({
    next: sanitizeNextPath(search.next),
  }),
  component: PreferencesPage,
});

function PreferencesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { next } = useSearch({ from: '/preferences' });

  const back = () => {
    if (next) {
      // `next` is a resolved path (e.g. `/share/study/<id>?dp=42`),
      // not a route template + params. TanStack's `to` field expects
      // the template form and will drop through to `/` for a raw
      // concrete path — use `href` to feed it verbatim (same pattern
      // `goToNextOrHome` uses after login).
      void navigate({ href: next });
    } else {
      void navigate({ to: '/' });
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <div className="mx-auto max-w-2xl px-6 py-8">
        <Button variant="ghost" size="sm" onClick={back} className="mb-4 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          {t('account.back', { defaultValue: 'Back' })}
        </Button>
        <h1 className="text-lg font-semibold text-foreground">
          {t('account.preferencesTitle', { defaultValue: 'Preferences' })}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('account.preferencesSubtitle', {
            defaultValue:
              'Language + theme apply to this browser. Personal account settings live at /account (sign-in required).',
          })}
        </p>

        {/* Same shell as the theme / language sections on `/account`
            (`<Card className="mt-6 p-5">`) so the public twin of that page
            and the signed-in one stay visually identical (ENG-1742). */}
        <Card className="mt-6 p-5">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t('account.theme')}
          </h2>
          <ThemePicker />
        </Card>

        <Card className="mt-6 p-5">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t('account.language')}
          </h2>
          <LanguagePicker />
        </Card>
      </div>
    </div>
  );
}
